import { useState } from "react";
import { z } from "zod";
import {
  PropertyRow,
  Text,
  WidgetEmptyAction,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";
import { ProjectStartFollowUpDialog } from "#app/components/updates/project-start-follow-up-dialog";
import { ProjectUpdateWidget } from "#app/components/updates/project-update-widget";
import { useIssueViewCapabilities } from "#app/components/issue-view/issue-view-capabilities";
import { useProjectCycleMutations } from "#app/hooks/issue-view/use-project-cycle-mutations";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { effectiveProjectCycle } from "#app/lib/project-updates";
import { useIssueWidgetContext } from "#app/widgets/issue/issue-widget-context";
import { IssueUpdatesHeaderActions } from "./header-actions";
import { IssueUpdatesHeaderCaption } from "./header-caption";
import { IssueUpdatesMenuItems } from "./menu-items";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";

export const issueUpdatesConfigSchema = z.looseObject({});
export type IssueUpdatesConfig = z.infer<typeof issueUpdatesConfigSchema>;

function IssueUpdatesRender(_: WidgetRenderContext<IssueUpdatesConfig>) {
  const ctx = useIssueWidgetContext();
  // The capability context, not `ctx`: the widget provider carries pre-gated
  // callbacks, and edit access here gates controls the widget owns outright
  // (the Cycle picker, the Start row). `issue-custom-fields`'s header actions
  // read it the same way, by direct path.
  const { canEdit } = useIssueViewCapabilities();
  const { workflowMap } = useWorkspaceData();
  const [startOpen, setStartOpen] = useState(false);
  const issue = ctx.issue;
  const age = issue?.age;
  const workflow = ctx.workflowId ? workflowMap.get(ctx.workflowId) : undefined;
  // Create mode never mounts this widget, so the id is always real by the time
  // a write can fire.
  const issueId = issue?.issueId ?? "";
  const { saveCycle, resetCycle } = useProjectCycleMutations(issueId);

  if (effectiveProjectCycle(age, ctx.stage, workflow).cycle == null) {
    // Nothing running reads the same on every host: ONE action row in the
    // header's place, never rows of "—". A reader who cannot start sees the
    // one fact instead.
    if (!canEdit) {
      return (
        <PropertyRow icon="hourglass" label="Cycle">
          <Text size="sm" variant="muted">
            Off
          </Text>
        </PropertyRow>
      );
    }
    return (
      <>
        <WidgetEmptyAction
          icon={PROPERTY_ICON.followUp}
          label="Start Follow up"
          onClick={() => setStartOpen(true)}
        />
        <ProjectStartFollowUpDialog
          via="follow_up_section"
          age={age}
          issueId={issueId}
          onOpenChange={setStartOpen}
          open={startOpen}
          stage={ctx.stage}
          workflow={workflow}
        />
      </>
    );
  }

  return (
    <ProjectUpdateWidget
      age={age}
      editable={canEdit}
      onCycleChange={(cycle) => {
        void saveCycle(cycle);
      }}
      onUseDefault={() => {
        // "Default" while already following the default writes nothing.
        if (age?.cycle) void resetCycle();
      }}
      stage={ctx.stage}
      workflow={workflow}
    />
  );
}

export const issueUpdatesDef: WidgetModule<IssueUpdatesConfig> = {
  type: "issue-updates",
  title: "Follow up",
  icon: PROPERTY_ICON.followUp,
  span: { minW: 3, minH: 1, maxW: 12, maxH: 12, defaultW: 12, defaultH: 3 },
  configSchema: issueUpdatesConfigSchema,
  headerActions: IssueUpdatesHeaderActions,
  headerCaption: IssueUpdatesHeaderCaption,
  menuItems: IssueUpdatesMenuItems,
  render: IssueUpdatesRender,
};
